// src/fabric/api.js
import { fetchAuthSession } from 'aws-amplify/auth';

import { RealtimeEventHandler } from './event_handler.js';
// import { RealtimeEventHandler } from '@openai/realtime-api-beta';
// import { RealtimeUtils } from '@openai/realtime-api-beta/lib/utils.js';
import { RealtimeUtils } from '@theodoreniu/realtime-api-beta';

export class FabricAPI extends RealtimeEventHandler {
  constructor({ url, apiKey, dangerouslyAllowAPIKeyInBrowser, debug } = {}) {
    super();
    // this.defaultUrl = 'wss://fabric.openai.com/v1';
    this.defaultUrl = 'wss://fabric.openai.com/v1';
    this.url = url || this.defaultUrl;
    this.username = null;
    this.models = null;
    this.realtimeApiKey = null;
    this.apiKey = apiKey || null;
    this.debug = !!debug;
    this.ws = null;

    if (globalThis.document && this.apiKey) {
      if (!dangerouslyAllowAPIKeyInBrowser) {
        throw new Error(
          'Can not provide API key in the browser without "dangerouslyAllowAPIKeyInBrowser" set to true'
        );
      }
    }
  }

  log(...args) {
    if (this.debug) {
      const date = new Date().toISOString();
      console.log(`[FabricAPI/${date}]`, ...args);
    }
    return true;
  }

  on(eventName, callback) {
    return super.on(eventName, callback);
  }

  isConnected() {
    return !!this.ws;
  }

  async connect(endpoint = 'fabric') {
    if (!this.apiKey && this.url === this.defaultUrl) {
      console.warn(`No apiKey provided for connection to "${this.url}"`);
    }
    if (this.isConnected()) {
      throw new Error('Already connected');
    }

    if (globalThis.document) {
      // Web browser implementation
      if (this.apiKey) {
        console.warn(
          'Warning: Connecting using API key in the browser, this is not recommended'
        );
      }
      const WebSocket = globalThis.WebSocket;

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken.toString();
      if (!token) {
        throw new Error('No ID token found in amplify session');
      }

      const ws = new WebSocket(`${this.url}/${endpoint}?token=${encodeURIComponent(token)}`);

      ws.addEventListener('message', (event) => {
        const message = JSON.parse(event.data);
        this.receive(message.type, message);
      });

      return new Promise((resolve, reject) => {
        const connectionErrorHandler = () => {
          this.disconnect(ws);
          reject(new Error(`Could not connect to "${this.url}"`));
        };

        ws.addEventListener('error', connectionErrorHandler);
        ws.addEventListener('open', () => {
          this.log(`Connected to "${this.url}"`);
          ws.removeEventListener('error', connectionErrorHandler);

          ws.addEventListener('error', () => {
            this.disconnect(ws);
            this.log(`Error, disconnected from "${this.url}"`);
            this.dispatch('close', { error: true });
          });

          ws.addEventListener('close', () => {
            this.disconnect(ws);
            this.log(`Disconnected from "${this.url}"`);
            this.dispatch('close', { error: false });
          });

          this.ws = ws;
          resolve(true);
        });
      });
    } else {
      // Node.js implementation
      const moduleName = 'ws';
      const wsModule = await import(/* webpackIgnore: true */ moduleName);
      const WebSocket = wsModule.default;
      const ws = new WebSocket(this.url, [], {
        finishRequest: (request) => {
          request.setHeader('Authorization', `Bearer ${this.apiKey}`);
          request.setHeader('OpenAI-Beta', 'fabric=v1');
          request.end();
        },
      });

      ws.on('message', (data) => {
        const message = JSON.parse(data.toString());
        this.receive(message.type, message);
      });

      return new Promise((resolve, reject) => {
        const connectionErrorHandler = () => {
          this.disconnect(ws);
          reject(new Error(`Could not connect to "${this.url}"`));
        };

        ws.on('error', connectionErrorHandler);
        ws.on('open', () => {
          this.log(`Connected to "${this.url}"`);
          ws.removeListener('error', connectionErrorHandler);

          ws.on('error', () => {
            this.disconnect(ws);
            this.log(`Error, disconnected from "${this.url}"`);
            this.dispatch('close', { error: true });
          });

          ws.on('close', () => {
            this.disconnect(ws);
            this.log(`Disconnected from "${this.url}"`);
            this.dispatch('close', { error: false });
          });

          this.ws = ws;
          resolve(true);
        });
      });
    }
  }

  disconnect(ws) {
    if (!ws || this.ws === ws) {
      this.ws && this.ws.close();
      this.ws = null;
      return true;
    }
  }

  receive(eventName, event) {
    this.log('received:', eventName, event);
    this.dispatch(`server.${eventName}`, event);
    this.dispatch('server.*', event);
    return true;
  }

  send(eventName, data = {}) {
    if (!this.isConnected()) {
      throw new Error('FabricAPI is not connected');
    }
    if (typeof data !== 'object') {
      throw new Error('data must be an object');
    }
    const event = {
      event_id: RealtimeUtils.generateId('fab_'),
      type: eventName,
      ...data,
    };
    this.dispatch(`client.${eventName}`, event);
    this.dispatch('client.*', event);
    this.log('sent:', eventName, event);
    this.ws.send(JSON.stringify(event));
    return true;
  }

  async fetch(endpoint, method = "GET", data = undefined, timeout = 86400000, isBlob = false) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken.toString();
      if (!token) {
        throw new Error('No ID token found in amplify session');
      }

      const request = {
        method: method,
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      };

      // Handle different types of data
      if (data instanceof FormData) {
        // Don't set Content-Type for FormData, browser will set it with boundary
        request.body = data;
      } else if (data && !isBlob) {
        request.headers['Content-Type'] = 'application/json';
        request.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.url}/${endpoint}`, request);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Return the entire response for blob handling
      if (isBlob) {
        return response;
      }

      return await response.json();
    } catch (error) {
      console.error('Server Unavailable:', error);
      throw error;
    }
  }

  async get(endpoint, timeout = 86400000) {
    return await this.fetch(endpoint, "GET", undefined, timeout);
  }

  async post(endpoint, data, timeout = 86400000) {
    return await this.fetch(endpoint, "POST", data, timeout);
  }
}